import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Download, Pencil, Trash2, History, X, Eye, Lock } from "lucide-react";
import { toast } from "sonner";
import { STATUS_BADGE, PO_STATUS_BADGE, PO_ROW_TINT, formatCurrency, formatNumber } from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import FieldEditor from "@/components/FieldEditor";
import CsvImportButton from "@/components/CsvImportButton";

export default function ModulePage() {
  const { moduleKey } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { perms } = useAuth();
  // Resolved CRUD permissions for this module + this user. Default to full CRUD
  // while perms are loading (they arrive with /auth/me before this page renders).
  const perm = (perms && perms[moduleKey]) || { read: true, create: true, update: true, delete: true };
  const canCreate = perm.create === true;
  const canDelete = perm.delete === true;
  const canUpdate = perm.update === true || (Array.isArray(perm.update) && perm.update.length > 0);
  const [meta, setMeta] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  // Initialise from URL so dashboard KPI links like /m/dispatch?status=Pending pre-apply the filter.
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);

  // Keep the URL in sync with the current status filter so links are shareable
  // and the browser back button restores the last view.
  useEffect(() => {
    const current = searchParams.get("status") || "all";
    if (current === statusFilter) return;
    const next = new URLSearchParams(searchParams);
    if (statusFilter === "all") next.delete("status");
    else next.set("status", statusFilter);
    setSearchParams(next, { replace: true });
  }, [statusFilter, searchParams, setSearchParams]);

  // If the user navigates between two KPI links (same route, new ?status=), reflect it.
  useEffect(() => {
    const fromUrl = searchParams.get("status") || "all";
    if (fromUrl !== statusFilter) setStatusFilter(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Reload on moduleKey change only; `api`/`toast` are stable module imports and
  // the setters are stable per React contract.
  useEffect(() => {
    setLoading(true);
    Promise.all([api.get("/meta/modules"), api.get(`/modules/${moduleKey}/records?limit=500`)])
      .then(([m, r]) => {
        const found = (m.data.modules || []).find((x) => x.key === moduleKey);
        setMeta(found);
        setRecords(r.data.items || []);
      })
      .catch(() => toast.error("Failed to load module"))
      .finally(() => setLoading(false));
  }, [moduleKey]);

  const reload = async () => {
    const r = await api.get(`/modules/${moduleKey}/records?limit=500`);
    setRecords(r.data.items || []);
  };

  const openCreate = () => {
    setFormData({});
    setDrawerOpen(true);
  };

  // Field Engineer Expense — auto-compute the Total from the five expense lines.
  useEffect(() => {
    if (moduleKey !== "engineer_expense") return;
    const t = ["travel_expense", "food_expense", "stay_expense", "local_conveyance", "other_expense"]
      .reduce((s, k) => s + (Number(formData[k]) || 0), 0);
    if ((Number(formData.total) || 0) !== t) setFormData((prev) => ({ ...prev, total: t }));
  }, [moduleKey, formData]);

  const submit = async () => {
    try {
      await api.post(`/modules/${moduleKey}/records`, { data: formData });
      toast.success("Created");
      setDrawerOpen(false);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/modules/${moduleKey}/records/${id}`);
      toast.success("Deleted");
      reload();
    } catch {
      toast.error("Delete failed");
    }
  };

  const openHistory = async (rec) => {
    setHistoryOpen(true);
    try {
      const { data } = await api.get(`/modules/${moduleKey}/records/${rec.id}/history`);
      setHistory(data.history || []);
    } catch {
      setHistory([]);
    }
  };

  const exportCsv = () => {
    // Cookie-based auth (same-origin /api) — no JWT in the URL.
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/modules/${moduleKey}/export`;
    window.open(url, "_blank");
  };

  // Resolve the actual status field key BEFORE filtering — many modules use
  // custom keys like `delivery_status`, `invoice_status`, `qc_status`, etc.
  const statusFieldKey = useMemo(
    () => meta?.fields?.find((f) => f.type === "status")?.key || "status",
    [meta]
  );

  // Filtered records — status filter now targets the module's real status field.
  const filtered = useMemo(() => {
    let list = records;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter((r) => JSON.stringify(r.data || {}).toLowerCase().includes(q));
    }
    if (statusFilter !== "all") {
      list = list.filter((r) => (r.data?.[statusFieldKey] || "") === statusFilter);
    }
    return list;
  }, [records, search, statusFilter, statusFieldKey]);

  if (loading || !meta) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  // Identify status field options
  const statusField = meta.fields.find((f) => f.type === "status");
  const statusOptions = statusField?.options || [];

  // Visible columns: show ALL non-textarea, non-attachment fields so the list
  // gives owners a full snapshot; the row's View button opens the complete
  // record (including textareas + attachments) in a new tab.
  const tableFields = meta.fields
    .filter((f) => f.type !== "attachments" && f.type !== "textarea");

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid={`module-page-${moduleKey}`}>
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">{meta.group}</div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">{meta.label}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {filtered.length} {filtered.length === 1 ? "record" : "records"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!canCreate && canUpdate && (
            <div className="flex items-center gap-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5" data-testid="limited-access-note">
              <Lock className="h-3 w-3" /> Read / limited-edit access
            </div>
          )}
          {!canCreate && !canUpdate && (
            <div className="flex items-center gap-1.5 text-[11px] text-slate-600 bg-slate-100 border border-slate-200 rounded px-2.5 py-1.5" data-testid="read-only-note">
              <Lock className="h-3 w-3" /> Read-only access
            </div>
          )}
          {canCreate && <CsvImportButton moduleKey={moduleKey} onImported={reload} />}
          <Button variant="outline" onClick={exportCsv} data-testid="module-export-button">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          {canCreate && (
            <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="module-create-button">
              <Plus className="h-4 w-4 mr-2" /> New Record
            </Button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <Card className="border-slate-200">
        <CardContent className="p-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search..."
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="module-search-input"
            />
          </div>
          {statusOptions.length > 0 && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48" data-testid="module-status-filter">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {statusOptions.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {(search || statusFilter !== "all") && (
            <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setStatusFilter("all"); }} data-testid="clear-filters">
              <X className="h-3.5 w-3.5 mr-1" /> Clear
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {tableFields.map((f) => (
                  <th key={f.key} className="text-left py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-slate-600">
                    {f.label}
                  </th>
                ))}
                <th className="text-right py-3 px-4 text-[11px] font-semibold uppercase tracking-wider text-slate-600 sticky right-0 bg-slate-50">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={tableFields.length + 1} className="text-center py-16 text-slate-500">
                    No records yet. Click "New Record" to add the first one.
                  </td>
                </tr>
              )}
              {filtered.map((rec) => (
                <tr
                  key={rec.id}
                  className={`border-b border-slate-100 ${moduleKey === "purchase_orders" ? PO_ROW_TINT(rec.data?.status) : "hover:bg-slate-50/60"}`}
                  data-testid={`row-${rec.id}`}
                >
                  {tableFields.map((f) => (
                    <td key={f.key} className="py-3 px-4 text-slate-700">
                      {renderCell(rec.data?.[f.key], f, moduleKey)}
                    </td>
                  ))}
                  <td className={`py-2 px-4 text-right sticky right-0 ${moduleKey === "purchase_orders" ? PO_ROW_TINT(rec.data?.status) : "bg-white"}`}>
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={`/m/${moduleKey}/${rec.id}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Open full view in new tab"
                        data-testid={`view-${rec.id}`}
                        className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100"
                      >
                        <Eye className="h-4 w-4 text-blue-600" />
                      </a>
                      <Button variant="ghost" size="icon" onClick={() => openHistory(rec)} title="History" data-testid={`history-${rec.id}`}>
                        <History className="h-4 w-4 text-slate-500" />
                      </Button>
                      {canUpdate && (
                        <a
                          href={`/m/${moduleKey}/${rec.id}/edit`}
                          target="_blank"
                          rel="noreferrer"
                          title="Edit in new tab"
                          data-testid={`edit-${rec.id}`}
                          className="inline-flex items-center justify-center h-9 w-9 rounded-md hover:bg-slate-100"
                        >
                          <Pencil className="h-4 w-4 text-slate-700" />
                        </a>
                      )}
                      {canDelete && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" title="Delete" data-testid={`delete-${rec.id}`}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
                            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel data-testid={`cancel-delete-${rec.id}`}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-red-600 hover:bg-red-700"
                              onClick={() => remove(rec.id)}
                              data-testid={`confirm-delete-${rec.id}`}
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Drawer Form — create only; editing happens full-page in a new tab */}
      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="record-drawer">
          <SheetHeader>
            <SheetTitle>New {meta.label.replace(/s$/, "")}</SheetTitle>
            <SheetDescription>
              Fill in the details. Required fields are marked.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-4">
            {meta.fields.map((f) => (
              <FieldEditor
                key={f.key}
                field={f}
                value={formData[f.key]}
                onChange={(v) => setFormData((prev) => ({ ...prev, [f.key]: v }))}
                disabled={moduleKey === "engineer_expense" && f.key === "total"}
              />
            ))}
            <div className="sticky bottom-0 -mx-6 px-6 py-4 bg-white border-t border-slate-200 mt-6 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDrawerOpen(false)} data-testid="drawer-cancel">Cancel</Button>
              <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="drawer-save">
                Create
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* History drawer */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Activity History</SheetTitle>
            <SheetDescription>All actions taken on this record.</SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-3">
            {history.length === 0 && <div className="text-sm text-slate-500">No activity recorded.</div>}
            {history.map((h) => (
              <div key={h._id} className="border-l-2 border-slate-200 pl-3">
                <div className="text-xs font-semibold text-slate-700 capitalize">{h.action}</div>
                <div className="text-xs text-slate-500">
                  {h.by_name || h.by_email} · {new Date(h.at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function renderCell(value, field, moduleKey) {
  if (value == null || value === "") return <span className="text-slate-400">—</span>;
  if (field.type === "status") {
    const badge = moduleKey === "purchase_orders" && field.key === "status"
      ? PO_STATUS_BADGE(value)
      : STATUS_BADGE(value);
    return <Badge variant="outline" className={badge}>{value}</Badge>;
  }
  if (field.type === "currency") return <span className="font-mono">{formatCurrency(value)}</span>;
  if (field.type === "number") return <span className="font-mono">{formatNumber(value)}</span>;
  if (field.type === "date") return value;
  if (typeof value === "string" && value.length > 60) return value.slice(0, 60) + "…";
  return value;
}
